'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Card, CardContent, IconButton, Typography } from '@mui/material';

import { ComponentErrorBoundary } from '@/components';
import { useT } from '@/hooks';
import { LeaderboardTrader, TimeFilter } from '@/types';
import { formatLeaderboardCurrency, getBiggestWinsTitle } from '@/utils';
import { BiggestWinsSkeleton } from './BiggestWinsSkeleton';
import { TraderHoverCard } from './TraderHoverCard';

import { ROUTES } from '@/constants';

interface BiggestWinRowProps {
  win: LeaderboardTrader;
  index: number;
}

const BiggestWinRow = ({ win, index }: BiggestWinRowProps) => {
  const rankDisplay = win?.winRank || '-';
  const userNameDisplay = win?.userName || '-';
  const eventTitleDisplay = win?.eventTitle || '-';
  const initialValueDisplay = formatLeaderboardCurrency(win?.initialValue || 0);
  const finalValueDisplay = formatLeaderboardCurrency(win?.finalValue || 0);

  const portfolioUrl = ROUTES.LEADERBOARD_PORTFOLIO(win?.proxyWallet || '0');

  const avatarBackground = useMemo(() => {
    const letters = '0123456789ABCDEF';
    const getRandomColor = () => {
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    };
    return `linear-gradient(135deg, ${getRandomColor()}, ${getRandomColor()})`;
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        py: 2.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none', pb: 0 },
      }}
    >
      {/* Rank */}
      <Typography
        sx={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'text.secondary',
          minWidth: 20,
        }}
      >
        {rankDisplay}
      </Typography>

      {/* Avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Link href={portfolioUrl} style={{ textDecoration: 'none' }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              background: avatarBackground,
              flexShrink: 0,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          />
        </Link>
      </Box>

      {/* Content: Name, Market, Amounts */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Name and Market */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <TraderHoverCard trader={win} index={index}>
            <Typography
              component={Link}
              href={portfolioUrl}
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                maxWidth: 120,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                flexShrink: 0,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main',
                },
              }}
            >
              {userNameDisplay}
            </Typography>
          </TraderHoverCard>
          <Typography
            component="span"
            sx={{ fontSize: '1rem', color: 'text.disabled', flexShrink: 0 }}
          >
            |
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '1rem',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {eventTitleDisplay}
          </Typography>
        </Box>

        {/* Financial Gain */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            component="span"
            sx={{ fontSize: '0.9375rem', fontWeight: 500, color: 'text.primary' }}
          >
            {initialValueDisplay}
          </Typography>
          <Typography component="span" sx={{ fontSize: '0.9375rem', color: 'text.disabled' }}>
            →
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: '0.9375rem', fontWeight: 500, color: '#4ec38a' }}
          >
            {finalValueDisplay}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface BiggestWinsProps {
  biggestWinsData: LeaderboardTrader[];
  selectedTimeFilter: TimeFilter;
  isLoading?: boolean;
  onClose?: () => void;
}

export const BiggestWins = ({
  biggestWinsData = [],
  selectedTimeFilter,
  isLoading,
  onClose,
}: BiggestWinsProps) => {
  const { t } = useT();
  const title = getBiggestWinsTitle(selectedTimeFilter, t);

  const renderWinRow = (win: LeaderboardTrader, index: number) => (
    <BiggestWinRow
      key={`${win?.userName || index}-${win?.winRank || index}`}
      win={win}
      index={index}
    />
  );

  const renderedWinsList = biggestWinsData?.map(renderWinRow);
  const hasData = biggestWinsData?.length > 0;

  if (isLoading) {
    return (
      <Box sx={{ height: 850, overflowY: 'scroll' }}>
        <Card
          sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2.5,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <BiggestWinsSkeleton />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <ComponentErrorBoundary componentName="Biggest Wins">
      <Box sx={{ height: 850, overflowY: 'scroll' }}>
        <Card
          sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2.5,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Box>
              {hasData ? (
                renderedWinsList
              ) : (
                <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  {t('common.noData')}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ComponentErrorBoundary>
  );
};
